import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * Layout-level signal for the "< Page Title" back link. Pages register
 * their breadcrumb with `useBreadcrumb({ label, onClick })`; Layout reads
 * the context and renders the link above the step pill nav.
 *
 * Why a context rather than rendering in-page: the breadcrumb sits above
 * the progress chrome that Layout owns, so the visual order needs to flow
 * Layout → breadcrumb → step list → children. The link's onClick is
 * page-owned state (e.g. ScreenHome's `setStep('intro')`), so URL-based
 * `<Link>` won't work — we pass a callback instead.
 */
export type BreadcrumbConfig = {
  label: string
  onClick: () => void
} | null

type BreadcrumbCtx = {
  breadcrumb: BreadcrumbConfig
  setBreadcrumb: (b: BreadcrumbConfig) => void
}

const Ctx = createContext<BreadcrumbCtx>({
  breadcrumb: null,
  setBreadcrumb: () => {},
})

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbConfig>(null)
  return <Ctx.Provider value={{ breadcrumb, setBreadcrumb }}>{children}</Ctx.Provider>
}

/**
 * Read-only access to the current breadcrumb, used by Layout to render.
 */
export function useBreadcrumbValue(): BreadcrumbConfig {
  return useContext(Ctx).breadcrumb
}

/**
 * Register a breadcrumb for the lifetime of the calling component. Pass
 * `null` to clear (or just unmount). The effect re-fires when `label` or
 * `onClick` change, so callers should keep `onClick` stable via `useCallback`
 * or accept the small re-register cost.
 */
export function useBreadcrumb(config: BreadcrumbConfig) {
  const { setBreadcrumb } = useContext(Ctx)
  useEffect(() => {
    setBreadcrumb(config)
    return () => setBreadcrumb(null)
  }, [config, setBreadcrumb])
}
